import React, { useCallback, useEffect, useRef, useState } from 'react';

import Spinner from '@atlaskit/spinner';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { token } from '@atlaskit/tokens';
import { useInitialUrl } from '../hooks/useInitialUrl.ts';
import DatabaseValueContainerContentUnifiedQuery, {
	type DatabaseValueContainerContentUnifiedQuery$data,
} from '../__generated__/DatabaseValueContainerContentUnifiedQuery.graphql.ts';
import type {
	DatabaseValueContainerContentUnifiedQuery_user_photos,
	DatabaseValueContainerContentUnifiedQuery_user_emails,
} from '../__types__/DatabaseValueContainerContentUnifiedQuery.ts';
import {
	queryInterop,
	type QueryInteropSupportedResult,
} from '@confluence/apollo-relay-interop/entry-points/query.ts';

type NullableString = string | null | undefined;

type Identity = {
	accountId: NullableString;
	nickname: NullableString;
	picture: NullableString;
	email: NullableString;
	locale: NullableString;
};
type Content = {
	contentReferenceId: NullableString;
	spaceId: NullableString;
	spaceKey: NullableString;
	spaceName: NullableString;
};

export type DatabaseValueContainerProps = {
	contentId: string;
	entryId: string;
	fieldId: string;
	isEdit?: boolean;
};

export const DatabaseValueContainer = React.memo(
	({ contentId, entryId, fieldId, isEdit }: DatabaseValueContainerProps) => {
		const iframeRef = useRef<HTMLIFrameElement>(null);
		const [content, setContent] = useState<Content | null>(null);
		const [dataLoaded, setDataLoaded] = useState(false);

		const [width, setWidth] = useState(0);
		const [height, setHeight] = useState(0);

		const databaseUrl = new URL(useInitialUrl(), window.location.origin);

		const [identity, setIdentity] = useState<Identity | null>(null);
		const { activationId, userId, cloudId } = useSessionData();

		useEffect(() => {
			void queryInterop<DatabaseValueContainerContentUnifiedQuery$data>({
				query: DatabaseValueContainerContentUnifiedQuery,
				variables: { contentId },
			}).then(
				({ data }: QueryInteropSupportedResult<DatabaseValueContainerContentUnifiedQuery$data>) => {
					setContent({
						contentReferenceId: data.content?.nodes?.[0]?.referenceId,
						spaceId: data.content?.nodes?.[0]?.space?.id,
						spaceKey: data.content?.nodes?.[0]?.space?.key,
						spaceName: data.content?.nodes?.[0]?.space?.name,
					});
					setIdentity({
						accountId: data?.user?.id,
						nickname: data?.user?.displayName,
						picture: data?.user?.photos?.find(
							(photo: DatabaseValueContainerContentUnifiedQuery_user_photos | null | undefined) =>
								photo?.isPrimary,
						)?.value,
						email: data?.user?.emails?.find(
							(email: DatabaseValueContainerContentUnifiedQuery_user_emails | null | undefined) =>
								email?.isPrimary,
						)?.value,
						locale: data?.user?.locale,
					});
					setDataLoaded(true);
				},
			);
		}, [contentId]);

		const bridgeData = {
			cloudId,
			activationId,
			userId,
			identity,
			contentId,
			...(content ? { content } : {}),
		};

		const messageCallback = useCallback((event: MessageEvent) => {
			const isFromExpectedIframe =
				iframeRef.current && event.source === iframeRef.current.contentWindow;

			if (isFromExpectedIframe) {
				try {
					const data = JSON.parse(event.data);
					if (data.method === 'resize' && 'width' in data && 'height' in data) {
						setWidth(data.width);
						setHeight(data.height);
					}
				} catch {}
			}
		}, []);

		useEffect(() => {
			window.addEventListener('message', messageCallback);

			return () => {
				window.removeEventListener('message', messageCallback);
			};
		}, [messageCallback]);

		if (entryId && fieldId && contentId) {
			databaseUrl.searchParams.delete('referencing');
			databaseUrl.searchParams.set('contentId', contentId);
			databaseUrl.searchParams.set('entryId', entryId);
			databaseUrl.searchParams.set('fieldId', fieldId);
			return (
				<>
					<iframe
						style={{
							width: `${width}px`,
							height: `${height}px`,
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							border: fg('confluence_frontend_tokenize_border') ? token('space.0') : '0px',
							pointerEvents: isEdit ? 'none' : 'auto',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							verticalAlign: 'middle',
						}}
						src={databaseUrl.toString()}
						{...(dataLoaded ? { 'data-bridge-data': JSON.stringify(bridgeData) } : {})}
						title="database-value-frame"
						ref={iframeRef}
					/>
					{width === 0 || height === 0 ? <Spinner testId="database-value-spinner" /> : null}
				</>
			);
		}

		return null;
	},
);
